import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Bell, Clock, CalendarPlus } from 'lucide-react';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly', days: 7 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'quarterly', label: 'Quarterly', days: 91 },
];

const freqDays = (f) => FREQUENCIES.find((x) => x.value === f)?.days || 30;

const Reminders = ({ session }) => {
  const [prefs, setPrefs] = useState({
    reminder_enabled: false,
    reminder_frequency: 'monthly',
    last_checkin_date: null,
  });
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [draft, setDraft] = useState({ reminder_enabled: false, reminder_frequency: 'monthly' });

  useEffect(() => {
    fetchPrefs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrefs = async () => {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('reminder_enabled, reminder_frequency, last_checkin_date')
        .eq('user_id', session.user.id)
        .single();
      if (!error && data) {
        setPrefs({
          reminder_enabled: data.reminder_enabled || false,
          reminder_frequency: data.reminder_frequency || 'monthly',
          last_checkin_date: data.last_checkin_date || null,
        });
      }
    } catch (error) {
      console.error('Error fetching reminder prefs:', error);
    } finally {
      setLoaded(true);
    }
  };

  const savePrefs = async (updates) => {
    setSaving(true);
    try {
      const next = { ...prefs, ...updates };
      const { error } = await supabase.from('user_preferences').upsert(
        {
          user_id: session.user.id,
          reminder_enabled: next.reminder_enabled,
          reminder_frequency: next.reminder_frequency,
          last_checkin_date: next.last_checkin_date,
        },
        { onConflict: 'user_id' }
      );
      if (error) throw error;
      setPrefs(next);
    } catch (error) {
      console.error('Error saving reminder prefs:', error);
      alert('Error saving reminder: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return Infinity;
    const ms = new Date().setHours(0, 0, 0, 0) - new Date(dateStr).setHours(0, 0, 0, 0);
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  const isDue =
    prefs.reminder_enabled && daysSince(prefs.last_checkin_date) >= freqDays(prefs.reminder_frequency);

  const markDone = () => {
    const today = new Date().toISOString().split('T')[0];
    savePrefs({ last_checkin_date: today });
    setDismissed(true);
  };

  const openModal = () => {
    setDraft({
      reminder_enabled: prefs.reminder_enabled,
      reminder_frequency: prefs.reminder_frequency,
    });
    setShowModal(true);
  };

  const handleSaveModal = async () => {
    await savePrefs({
      reminder_enabled: draft.reminder_enabled,
      reminder_frequency: draft.reminder_frequency,
    });
    setDismissed(false);
    setShowModal(false);
  };

  const downloadIcs = () => {
    const freq = draft.reminder_frequency;
    let rrule = 'FREQ=MONTHLY';
    if (freq === 'weekly') rrule = 'FREQ=WEEKLY';
    else if (freq === 'quarterly') rrule = 'FREQ=MONTHLY;INTERVAL=3';

    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const dateStamp = `${y}${m}${d}`;

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Lahn Lab//Net Worth Reminder//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:networth-checkin-${dateStamp}@yourmoneytool`,
      `DTSTART;VALUE=DATE:${dateStamp}`,
      `RRULE:${rrule}`,
      'SUMMARY:💰 Net Worth Check-in',
      'DESCRIPTION:Time to update your assets, liabilities, and take a net worth snapshot to stay on track.',
      'BEGIN:VALARM',
      'TRIGGER:-PT9H',
      'ACTION:DISPLAY',
      'DESCRIPTION:Net Worth Check-in',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'networth-checkin.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!loaded) return null;

  const freqLabel = FREQUENCIES.find((f) => f.value === prefs.reminder_frequency)?.label || 'Monthly';

  return (
    <>
      {/* Due banner */}
      {isDue && !dismissed && (
        <div className="mb-4 flex items-center justify-between gap-4 bg-warning-50 border border-warning-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-warning-800">
            <Bell className="h-5 w-5 text-warning-600 shrink-0" />
            <span>
              <strong>Time for your {freqLabel.toLowerCase()} check-in!</strong> Update your numbers and take a snapshot to stay on track.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={markDone}
              disabled={saving}
              className="px-3 py-1.5 bg-warning-600 hover:bg-warning-700 disabled:bg-warning-400 text-white rounded-md text-sm font-medium"
            >
              Mark done
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="text-warning-700 hover:text-warning-900 text-sm px-2"
            >
              Later
            </button>
          </div>
        </div>
      )}

      {/* Reminder control bar */}
      <div className="mb-4 flex items-center justify-between gap-4 card px-4 py-3">
        <div className="text-sm text-gray-600">
          {prefs.reminder_enabled ? (
            <span>
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 align-middle" />
              {freqLabel} check-in reminder is on
              {prefs.last_checkin_date && (
                <span className="text-gray-400"> · last check-in {new Date(prefs.last_checkin_date).toLocaleDateString()}</span>
              )}
            </span>
          ) : (
            <span>
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-2 align-middle" />
              No check-in reminder set
            </span>
          )}
        </div>
        <button onClick={openModal} className="btn-outline btn-sm shrink-0">
          <Clock className="h-4 w-4 mr-1.5" /> Set Reminder
        </button>
      </div>

      {/* Settings modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Stay-on-track Reminder</h3>
            <p className="text-sm text-gray-500 mb-4">
              We'll nudge you with an in-app banner when a check-in is due. Add it to your calendar for reminders even when the app is closed.
            </p>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.reminder_enabled}
                onChange={(e) => setDraft({ ...draft, reminder_enabled: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable in-app check-in reminder</span>
            </label>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">How often?</label>
              <div className="grid grid-cols-3 gap-2">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setDraft({ ...draft, reminder_frequency: f.value })}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors duration-200 ${
                      draft.reminder_frequency === f.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={downloadIcs} className="btn-outline w-full mb-5">
              <CalendarPlus className="h-4 w-4 mr-2" /> Add to calendar (.ics)
            </button>

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveModal}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Reminders;
